import { A } from '@solidjs/router'
import { cn } from '@veem/ui'
import { type JSX, splitProps } from 'solid-js'

/**
 * Prototype button. Replaces the shared Button once the look is approved.
 *
 * Depth comes from a hairline ring, a 1px top highlight and a short drop shadow rather than from
 * a flat fill, so a primary action reads as a physical key. The press shrinks on pointer-down
 * and recovers on release; colour changes are not animated on hover-in, only on hover-out.
 */
const base = [
  'inline-flex shrink-0 select-none items-center justify-center gap-1.5 whitespace-nowrap font-medium',
  'rounded-[0.5rem] text-body [&_svg]:size-4 [&_svg]:shrink-0',
  'transition-[background-color,box-shadow,color,transform] duration-(--duration-press) ease-out',
  'active:scale-[0.97] active:duration-0 motion-reduce:active:scale-100',
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
  'disabled:pointer-events-none disabled:opacity-45',
]

const variants = {
  primary: [
    'bg-brand-700 text-text-inverse',
    'shadow-[inset_0_1px_0_oklch(1_0_0/0.16),0_0_0_1px_var(--color-brand-800),0_1px_2px_oklch(0.196_0.007_265/0.24)]',
    '[@media(hover:hover)]:hover:bg-brand-600',
  ],
  secondary: [
    'bg-surface text-text',
    'shadow-[0_0_0_1px_var(--color-border-strong),0_1px_2px_oklch(0.196_0.007_265/0.06)]',
    '[@media(hover:hover)]:hover:bg-surface-muted [@media(hover:hover)]:hover:shadow-[0_0_0_1px_oklch(0.8_0.005_265),0_1px_2px_oklch(0.196_0.007_265/0.08)]',
  ],
  success: [
    'bg-status-done text-text-inverse',
    'shadow-[inset_0_1px_0_oklch(1_0_0/0.18),0_0_0_1px_oklch(0.48_0.11_158),0_1px_2px_oklch(0.196_0.007_265/0.2)]',
    '[@media(hover:hover)]:hover:brightness-105',
  ],
  ghost: [
    'text-text-muted',
    '[@media(hover:hover)]:hover:bg-surface-sunken [@media(hover:hover)]:hover:text-text',
  ],
} as const

const sizes = {
  sm: 'h-7 px-2.5 max-md:h-(--size-touch)',
  md: 'h-8 px-3 max-md:h-(--size-touch)',
  icon: 'size-8 max-md:size-(--size-touch)',
} as const

export type BtnVariant = keyof typeof variants
export type BtnSize = keyof typeof sizes

export const btnClass = (variant: BtnVariant = 'primary', size: BtnSize = 'md', extra?: string) =>
  cn(base, variants[variant], sizes[size], extra)

export const Btn = (
  props: JSX.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: BtnVariant; size?: BtnSize },
) => {
  const [local, rest] = splitProps(props, ['variant', 'size', 'class'])
  return <button type="button" {...rest} class={btnClass(local.variant, local.size, local.class)} />
}

export const BtnLink = (props: {
  href: string
  variant?: BtnVariant
  size?: BtnSize
  class?: string
  children: JSX.Element
}) => (
  <A href={props.href} class={btnClass(props.variant, props.size, props.class)}>
    {props.children}
  </A>
)

/** Shortcut hint that sits inside a button and matches its fill. */
export const BtnKbd = (props: { children: string; tone?: 'onFill' | 'onSurface' }) => (
  <kbd
    class={cn(
      'ml-0.5 inline-grid h-[1.125rem] min-w-[1.125rem] place-items-center rounded-[0.3125rem] px-1 font-mono text-[0.625rem] font-semibold max-md:hidden',
      props.tone === 'onSurface'
        ? 'bg-surface-sunken text-text-subtle shadow-[0_0_0_1px_var(--color-border)]'
        : 'bg-white/14 text-white/80 shadow-[inset_0_0_0_1px_oklch(1_0_0/0.18)]',
    )}
  >
    {props.children}
  </kbd>
)
